import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import useDetailTable from '#/hooks/table/useDetailTable';
import DetailTable from '#/modules/commonComponents/table/DetailTable';
import PurchaseAmendmentPreviewDialog from '#/modules/srmMgmt/purchaseOrder/components/PurchaseAmendmentPreviewDialog';
import { buildPurchaseOrderAmendmentColumns } from '#/modules/srmMgmt/purchaseOrder/data/purchaseOrderAmendmentColumns';
import {
  PURCHASE_AMENDMENT_KIND,
} from '#/modules/srmMgmt/purchaseOrder/helper/purchaseAmendmentKinds';
import { sortPurchaseAmendmentsDesc } from '#/modules/srmMgmt/purchaseOrder/helper/purchaseAmendmentStatus';

/**
 * Detail 異動頁籤（物料／非庫存採購共用）：`detail.amendments` 列表＋預覽對話框。
 * 預覽內容另打異動單明細（before／after、擬議付款期別）。
 */
const PurchaseOrderAmendmentsSection = ({
  kind = PURCHASE_AMENDMENT_KIND.PURCHASE_ORDER,
  detail,
  onRefetch,
}) => {
  const [previewAmendmentId, setPreviewAmendmentId] = useState(null);
  const columns = useMemo(
    () => buildPurchaseOrderAmendmentColumns({
      onPreview: (row) => setPreviewAmendmentId(row?.id ?? null),
    }),
    [],
  );
  const rows = useMemo(
    () => sortPurchaseAmendmentsDesc(detail?.amendments).map((row, index) => ({
      ...row,
      _rowKey: row?.id != null && row.id !== ''
        ? `${row.id}-${index}`
        : `po-amendment-${index}`,
    })),
    [detail?.amendments],
  );

  const detailTableState = useDetailTable(rows, columns, {
    enableExpanding: false,
    getRowId: (row) => String(row._rowKey),
  });

  return (
    <>
      {rows.length === 0 ? (
        <p className="px-4 py-5 text-sm text-muted-foreground">尚無異動紀錄</p>
      ) : (
        <div className="detail-table-container">
          <DetailTable
            state={detailTableState}
            containerClassName="detail-table-scroll--section-embedded"
            tableClassName="detail-table--flat-lines"
          />
        </div>
      )}
      <PurchaseAmendmentPreviewDialog
        kind={kind}
        open={Boolean(previewAmendmentId)}
        onOpenChange={(open) => {
          if (!open) {
            setPreviewAmendmentId(null);
          }
        }}
        amendmentId={previewAmendmentId}
        order={detail}
        onMutated={onRefetch}
      />
    </>
  );
};

PurchaseOrderAmendmentsSection.propTypes = {
  kind: PropTypes.oneOf(Object.values(PURCHASE_AMENDMENT_KIND)),
  detail: PropTypes.shape({
    id: PropTypes.string,
    lines: PropTypes.arrayOf(PropTypes.object),
    amendments: PropTypes.arrayOf(PropTypes.object),
  }),
  onRefetch: PropTypes.func,
};

export default PurchaseOrderAmendmentsSection;
